// Lightweight internationalisation helpers for Phase 1.
// - Country list (ISO 3166-1 alpha-2 subset we actively support in RollM8s)
// - Currency code + symbol resolution by country
// - Currency formatting helper that accepts minor units (pence/cents)

export const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', dialCode: '+44' },
  { code: 'IE', name: 'Ireland',        flag: '🇮🇪', currency: 'EUR', dialCode: '+353' },
  { code: 'US', name: 'United States',  flag: '🇺🇸', currency: 'USD', dialCode: '+1' },
  { code: 'CA', name: 'Canada',         flag: '🇨🇦', currency: 'CAD', dialCode: '+1' },
  { code: 'AU', name: 'Australia',      flag: '🇦🇺', currency: 'AUD', dialCode: '+61' },
  { code: 'NZ', name: 'New Zealand',    flag: '🇳🇿', currency: 'NZD', dialCode: '+64' },
  { code: 'FR', name: 'France',         flag: '🇫🇷', currency: 'EUR', dialCode: '+33' },
  { code: 'DE', name: 'Germany',        flag: '🇩🇪', currency: 'EUR', dialCode: '+49' },
  { code: 'ES', name: 'Spain',          flag: '🇪🇸', currency: 'EUR', dialCode: '+34' },
  { code: 'IT', name: 'Italy',          flag: '🇮🇹', currency: 'EUR', dialCode: '+39' },
  { code: 'NL', name: 'Netherlands',    flag: '🇳🇱', currency: 'EUR', dialCode: '+31' },
  { code: 'BE', name: 'Belgium',        flag: '🇧🇪', currency: 'EUR', dialCode: '+32' },
  { code: 'PT', name: 'Portugal',       flag: '🇵🇹', currency: 'EUR', dialCode: '+351' },
  { code: 'PL', name: 'Poland',         flag: '🇵🇱', currency: 'PLN', dialCode: '+48' },
  { code: 'SE', name: 'Sweden',         flag: '🇸🇪', currency: 'SEK', dialCode: '+46' },
  { code: 'NO', name: 'Norway',         flag: '🇳🇴', currency: 'NOK', dialCode: '+47' },
  { code: 'DK', name: 'Denmark',        flag: '🇩🇰', currency: 'DKK', dialCode: '+45' },
  { code: 'FI', name: 'Finland',        flag: '🇫🇮', currency: 'EUR', dialCode: '+358' },
  { code: 'CH', name: 'Switzerland',    flag: '🇨🇭', currency: 'CHF', dialCode: '+41' },
  { code: 'JP', name: 'Japan',          flag: '🇯🇵', currency: 'JPY', dialCode: '+81' },
  { code: 'MX', name: 'Mexico',         flag: '🇲🇽', currency: 'MXN', dialCode: '+52' },
  { code: 'BR', name: 'Brazil',         flag: '🇧🇷', currency: 'BRL', dialCode: '+55' },
  { code: 'ZA', name: 'South Africa',   flag: '🇿🇦', currency: 'ZAR', dialCode: '+27' },
  { code: 'IN', name: 'India',          flag: '🇮🇳', currency: 'INR', dialCode: '+91' },
  { code: 'AE', name: 'UAE',            flag: '🇦🇪', currency: 'AED', dialCode: '+971' },
];

const COUNTRY_MAP = Object.fromEntries(COUNTRIES.map(c => [c.code, c]));

// Countries that primarily use imperial units (miles, mph).
export const IMPERIAL_COUNTRIES = new Set(['GB', 'US']);

export const getCountry = (code) => COUNTRY_MAP[(code || '').toUpperCase()] || null;
export const getCountryFlag = (code) => getCountry(code)?.flag || '';
export const getCountryName = (code) => getCountry(code)?.name || code || '';
export const getCurrencyFor = (code) => getCountry(code)?.currency || 'GBP';
export const getDialCode = (code) => getCountry(code)?.dialCode || '+44';
export const usesImperial = (code) => IMPERIAL_COUNTRIES.has((code || '').toUpperCase());

// Format `amount` (can be minor units like pence if `minor=true`) for the
// currency associated with `countryCode` (or explicit `currency`).
export const formatCurrency = (amount, { countryCode, currency, minor = false } = {}) => {
  if (amount == null || isNaN(amount)) return '';
  const ccy = currency || getCurrencyFor(countryCode);
  const value = minor ? amount / 100 : amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccy,
      maximumFractionDigits: ccy === 'JPY' ? 0 : 2,
    }).format(value);
  } catch {
    return `${ccy} ${value.toFixed(2)}`;
  }
};

// Guess user's country from the browser when not set on profile.
export const detectBrowserCountry = () => {
  try {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale || navigator.language || 'en-GB';
    const parts = locale.split('-');
    const region = parts[parts.length - 1]?.toUpperCase();
    if (region && region.length === 2 && COUNTRY_MAP[region]) return region;
  } catch {}
  return 'GB';
};

// Distance formatting — km for metric countries, miles for imperial.
// `meters` is the canonical input. Returns "12.3 km" or "7.6 mi".
export const formatDistance = (meters, { countryCode } = {}) => {
  if (meters == null || isNaN(meters)) return '';
  if (usesImperial(countryCode)) {
    const miles = meters / 1609.344;
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
  }
  const km = meters / 1000;
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
};

// Speed formatting — kph for metric, mph for imperial.
// `mps` is metres per second (canonical geolocation speed unit).
export const formatSpeed = (mps, { countryCode } = {}) => {
  if (mps == null || isNaN(mps)) return '';
  if (usesImperial(countryCode)) {
    return `${Math.round(mps * 2.23694)} mph`;
  }
  return `${Math.round(mps * 3.6)} km/h`;
};

// Localised date formatting using the user's country locale.
// Accepts Date, ISO string, or ms timestamp. Options mirror Intl.DateTimeFormat.
export const formatDate = (value, { countryCode, options } = {}) => {
  if (value == null || value === '') return '';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d)) return '';
    const locale = countryCode ? `en-${countryCode.toUpperCase()}` : undefined;
    return new Intl.DateTimeFormat(locale, options || { dateStyle: 'medium' }).format(d);
  } catch {
    return String(value);
  }
};
